import React, {
  Children,
  FC,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ButtonBase, Collapse } from '@material-ui/core';
import {
  ConversationType,
  selectCurrentSelectedChat,
  selectGroupMetadataId,
  selectPrivateMetadataId,
  selectTwilioListCollapseState,
  setCollapseState,
  selectAnnouncementMetadata,
  setSelectedChat,
  selectRelevantMetdataCount,
  selectPublicMetadataId,
  haveAllConversationsLoaded,
  UserReachabilityStatus,
} from 'models/twilio';
import { ExpandMoreRounded } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import UserAvatar from './styled/UserAvatar';
import { MemoedChatBadge } from './styled/ChatBadge';
import useConversationListItem from '../hooks/useConversationListItem';
import { BEHAVIOR, COLORS } from '../constants';
import ConversationListPlaceholder from './placeholders/ConversationListPlaceholder';
import {
  ChatTitleContainer,
  ChatTitleSubtitleGroup,
} from './CommonChatDisplayComponents';
import ConverastionListItemSkeleton from './placeholders/ConversationListItemSkeleton';
import TRANSLATIONS from '../translations';

const ConversationListItemButton = styled(ButtonBase)<{ $isSelected: boolean }>`
  height: fit-content;
  cursor: pointer;
  display: flex;
  padding: 12px 16px;
  transition: background-color 0.5s ease;
  background-color: ${({ $isSelected }) => ($isSelected ? COLORS.GREY_M : 'unset')};
  align-items: stretch;
  width: 100%;
  color: ${COLORS.TEXT_DARK};

  &:hover {
    background-color: ${COLORS.GREY_M};
  }
`;

const StyledChatTitleSubtitleGroup = styled(ChatTitleSubtitleGroup)`
  margin-left: 10px;
  justify-content: space-evenly;

  & ${ChatTitleContainer} {
    font-size: 14px;
    line-height: 14px;
  }
`;

const DateTimeContainer = styled.div`
  width: fit-content;
  font-family: Lato, sans-serif;
  font-size: 12px;
  white-space: nowrap;
`;

interface ItemInformationProps {
  displayName: string;
  messagePreview: string;
}

const ItemInformation: FC<ItemInformationProps> = React.memo(({ displayName, messagePreview }: ItemInformationProps) => (
  <StyledChatTitleSubtitleGroup
    title={displayName}
    subtitle={messagePreview}
  />
));

export interface ConversationListItemProps {
  metadataId: string;
}

const ConversationListItem: FC<ConversationListItemProps> = React.memo(({ metadataId }: ConversationListItemProps) => {
  const dispatch = useDispatch();
  const skeletonRef = useRef<HTMLButtonElement>();
  const isSelected = useSelector(selectCurrentSelectedChat) === metadataId;

  const {
    displayName,
    reachabilityStatus,
    unreadCount,
    messagePreview,
    timeString,
    iconSrc,
    isLoading,
  } = useConversationListItem(metadataId);

  useLayoutEffect(() => {
    setTimeout(() => {
      if (skeletonRef.current) {
        skeletonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, BEHAVIOR.CHAT_ANIMATION_TIMEOUT);
  }, [skeletonRef]);

  const onListItemClick = useCallback(() => {
    dispatch(setSelectedChat(isSelected ? null : metadataId));
  }, [dispatch, metadataId, isSelected]);

  const memoedTime = useMemo(() => (
    timeString && <DateTimeContainer>{timeString}</DateTimeContainer>
  ), [timeString]);

  return useMemo(() => {
    if (isLoading) {
      return (
        <ConversationListItemButton
          ref={skeletonRef}
          $isSelected={false}
          disabled
        >
          <ConverastionListItemSkeleton animation="pulse" />
        </ConversationListItemButton>
      );
    }
    if (!displayName) return null;
    return (
      <ConversationListItemButton
        $isSelected={isSelected}
        onClick={onListItemClick}
      >
        <MemoedChatBadge
          unreadCount={unreadCount}
          displayOnlineDot={reachabilityStatus === UserReachabilityStatus.ONLINE}
        >
          <UserAvatar name={displayName} src={iconSrc} />
        </MemoedChatBadge>
        <ItemInformation displayName={displayName} messagePreview={messagePreview} />
        {memoedTime}
      </ConversationListItemButton>
    );
  }, [displayName, iconSrc, isLoading, memoedTime, messagePreview, onListItemClick, unreadCount, isSelected, reachabilityStatus]);
});

interface CollapsibleWrapperProps {
  category: ConversationType;
  children: ReactNode;
}

const StyledCollapseButton = styled(ButtonBase)`
  font-family: Lato, sans-serif;
  font-size: 14px;
  width: 100%;
  color: ${COLORS.TEXT_DARK};
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.5s ease;

  :hover {
    background-color: ${COLORS.GREY_M};
  }

  display: flex;
  justify-content: flex-start;
`;

const ExpandIcon = styled(ExpandMoreRounded)<{ $isExpanded: boolean }>`
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(0deg)' : 'rotate(-180deg)')};
  transition: transform 0.2s ease;
  margin-right: 10px;
  fill: ${COLORS.TEXT_DARK};
`;

const CollapsibleWrapper: FC<CollapsibleWrapperProps> = React.memo(({ category, children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isExpanded = useSelector(selectTwilioListCollapseState(category));
  const numChildren = Children.toArray(children).length;

  const buttonText = useMemo(() => {
    const translations: Record<ConversationType, string> = {
      [ConversationType.ANNOUNCEMENT]: t(...TRANSLATIONS.JOINED_ROOMS.ANNOUNCEMENT),
      [ConversationType.PRIVATE]: t(...TRANSLATIONS.JOINED_ROOMS.PRIVATE),
      [ConversationType.GROUP]: t(...TRANSLATIONS.JOINED_ROOMS.GROUP),
      [ConversationType.PUBLIC]: t(...TRANSLATIONS.JOINED_ROOMS.PUBLIC),
      [ConversationType.UNKNOWN]: null,
    };
    return translations[category] ?? null;
  }, [category, t]);

  const onClick = useCallback(() => {
    dispatch(setCollapseState({ type: category, value: !isExpanded }));
  }, [dispatch, category, isExpanded]);

  if (!numChildren) return null;

  return (
    <>
      <StyledCollapseButton onClick={onClick}>
        <ExpandIcon $isExpanded={isExpanded} />
        {buttonText}
      </StyledCollapseButton>
      <Collapse in={isExpanded}>
        {children}
      </Collapse>
    </>
  );
});

const ConversationList: FC = () => {
  const privateMetadataIds = useSelector(selectPrivateMetadataId, shallowEqual);
  const groupMetadataIds = useSelector(selectGroupMetadataId, shallowEqual);
  const publicMetadataIds = useSelector(selectPublicMetadataId, shallowEqual);
  const announcementMetadata = useSelector(selectAnnouncementMetadata, shallowEqual);
  const conversationCount = useSelector(selectRelevantMetdataCount, shallowEqual);
  const allConversationsLoaded = useSelector(haveAllConversationsLoaded);

  if (!allConversationsLoaded) {
    return (
      <>
        {Array.from(Array(8).keys()).map((i) => (
          <ConversationListItemButton key={`SkeletonListItem-${i}`} $isSelected={false} disabled>
            <ConverastionListItemSkeleton animation="pulse" />
          </ConversationListItemButton>
        ))}
      </>
    );
  }
  if (!conversationCount) return <ConversationListPlaceholder />;

  return (
    <>
      <CollapsibleWrapper category={ConversationType.ANNOUNCEMENT}>
        {announcementMetadata?.id && <ConversationListItem key={`ListItem-${announcementMetadata.id}`} metadataId={announcementMetadata.id} />}
      </CollapsibleWrapper>
      <CollapsibleWrapper category={ConversationType.PRIVATE}>
        {privateMetadataIds.map((id) => <ConversationListItem key={`ListItem-${id}`} metadataId={id} />)}
      </CollapsibleWrapper>
      <CollapsibleWrapper category={ConversationType.GROUP}>
        {groupMetadataIds.map((id) => <ConversationListItem key={`ListItem-${id}`} metadataId={id} />)}
      </CollapsibleWrapper>
      <CollapsibleWrapper category={ConversationType.PUBLIC}>
        {publicMetadataIds.map((id) => <ConversationListItem key={`ListItem-${id}`} metadataId={id} />)}
      </CollapsibleWrapper>
    </>
  );
};

export default ConversationList;
